import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import {
  PaginationDropdown,
  ThinDropdown,
  ThinTextField,
} from "../common/GeneralComponent";
import { fetchData } from "../../utils/functions";
import { useDispatch } from "react-redux";
import GeneralFooter from "../common/GeneralFooter";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { Minus, Plus, Eye } from "lucide-react";
import { toast } from "react-toastify";

const MonitoringPhaseListing = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [details, setDetails] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [search, setSearch] = useState("");
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [filterVillageName, setFilterVillageName] = useState("");
  const [filterCondition, setFilterCondition] = useState("");
  const [filterFunctionalStatus, setFilterFunctionalStatus] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };

  const fetchMonitoringRecords = async (page = 1, pageSize = 10, searchTerm = "") => {
    try {
      const response = await fetchData(
        `monitoring-phase?page=${page}&limit=${pageSize}&search=${searchTerm}`,
        "GET",
        null,
        {},
        {},
        navigate,
        null,
        dispatch
      );

      if (response.success && Array.isArray(response.data)) {
        const tableData = response.data.map((item) => ({
          boqs_proposed_monitoring_id: item.boqs_proposed_monitoring_id,
          village_id: item.village_id,
          village_name: item.village_name || "N/A",
          district_name: item.district_name || "N/A",
          feature_name: item.feature_name || "N/A",
          category_name: item.category_name || "N/A",
          date_of_visit: item.date_of_visit || null,
          condition_observed: item.condition_observed || "N/A",
          feature_currently_functional:
            item.feature_currently_functional || "N/A",
          immediate_follow_up: item.immediate_follow_up || "N/A",
          created_at: item.created_at || null,
        }));

        setDetails(tableData);
        setRolesData(tableData);

        // Use pagination data from API response
        setPagination({
          currentPage: response.pagination.page,
          pageSize: response.pagination.limit,
          totalItems: response.pagination.total,
          totalPages: response.pagination.totalPages,
        });
      } else {
        setDetails([]);
        setRolesData([]);
        console.warn("Unexpected response format:", response);
      }
    } catch (error) {
      console.error("Failed to fetch monitoring records:", error);
      toast.error("Failed to fetch monitoring records");
    }
  };

useEffect(() => {
  fetchMonitoringRecords(pagination.currentPage, pagination.pageSize, search);
}, [pagination.currentPage, pagination.pageSize, search]);

  const villageOptions = rolesData
    .map((record) => ({
      label: record.village_name,
      value: record.village_name,
    }))
    .filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i);

  const conditionOptions = [
    { label: "Good", value: "Good" },
    { label: "Minor Issues", value: "Minor Issues" },
    { label: "Major Issues", value: "Major Issues" },
    { label: "Non-functional", value: "Non-functional" },
  ];

  const functionalStatusOptions = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
    { label: "Partially", value: "Partially" },
  ];

  const filteredRecords = rolesData.filter((record) => {
    const matchesSearch =
      record.village_name.toLowerCase().includes(search.toLowerCase()) ||
      record.feature_name.toLowerCase().includes(search.toLowerCase()) ||
      record.category_name.toLowerCase().includes(search.toLowerCase());

    const matchesVillageFilter = filterVillageName
      ? record.village_name === filterVillageName
      : true;
    const matchesConditionFilter = filterCondition
      ? record.condition_observed === filterCondition
      : true;

    const matchesFunctionalFilter = filterFunctionalStatus
      ? record.feature_currently_functional === filterFunctionalStatus
      : true;

    return (
      matchesSearch &&
      matchesVillageFilter &&
      matchesConditionFilter &&
      matchesFunctionalFilter
    );
  });

  const handleApply = () => {
    if (!filterVillageName && !filterCondition && !filterFunctionalStatus) {
      //toast.error("Please select at least one filter to apply");
      return;
    }
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
// Use filteredRecords directly since API handles pagination
const paginatedRecords = filteredRecords;

  const handlePageChange = (event, value) => {
  setPagination((prev) => ({ ...prev, currentPage: value }));
  // fetchMonitoringRecords will be called by useEffect
};
const handlePageSizeChange = (size) => {
  setPagination((prev) => ({
    ...prev,
    pageSize: size,
    currentPage: 1,
  }));
  // fetchMonitoringRecords will be called by useEffect
};

  // useEffect(() => {
  //   const totalPages = Math.ceil(filteredRecords.length / pagination.pageSize);
  //   setPagination((prev) => ({
  //     ...prev,
  //     totalItems: filteredRecords.length,
  //     totalPages,
  //   }));
  // }, [filteredRecords.length, pagination.pageSize]);

  const columns = [
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Sr.No</span>
      ),
      cell: (row, index) => <span>{startIndex + index + 1}</span>,
      width: "80px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Village</span>
      ),
      selector: (row) => row.village_name || "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          District
        </span>
      ),
      selector: (row) => row.district_name || "N/A",
      sortable: true,
      width: "130px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Feature</span>
      ),
      selector: (row) => row.feature_name || "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Category
        </span>
      ),
      selector: (row) => row.category_name || "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Date of Visit
        </span>
      ),
      selector: (row) =>
        row.date_of_visit
          ? new Date(row.date_of_visit).toLocaleDateString()
          : "N/A",
      sortable: true,
      width: "130px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Condition
        </span>
      ),
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-md text-xs font-semibold ${
            row.condition_observed === "Good"
              ? "bg-green-100 text-green-800"
              : row.condition_observed === "Minor Issues"
              ? "bg-yellow-100 text-yellow-800"
              : row.condition_observed === "Major Issues"
              ? "bg-orange-100 text-orange-800"
              : row.condition_observed === "Non-functional"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {row.condition_observed}
        </span>
      ),
      sortable: true,
      width: "150px",
      center: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Functional
        </span>
      ),
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-md text-xs font-semibold ${
            row.feature_currently_functional === "Yes"
              ? "bg-green-100 text-green-800"
              : row.feature_currently_functional === "Partially"
              ? "bg-yellow-100 text-yellow-800"
              : row.feature_currently_functional === "No"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {row.feature_currently_functional}
        </span>
      ),
      sortable: true,
      width: "150px",
      center: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Action</span>
      ),
      cell: (row) => (
        <button
          onClick={() =>
            navigate(
              `/monitoring-phase/view/${row.boqs_proposed_monitoring_id}`
            )
          }
          className="bg-[#137e81] hover:bg-[#0f6b6e] text-white px-3 py-1 rounded-md font-semibold transition-colors duration-200 flex items-center gap-1"
        >
          <Eye size={16} />
          View
        </button>
      ),
      center: true,
      width: "120px",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "#fff",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  return (
    <div className="w-full">
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Monitoring Phase Information
          </h1>
        </div>

        <Accordion expanded={isFiltersVisible} onChange={toggleFilters}>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#ffff" size={14} />
              ) : (
                <Plus color="#ffff" size={14} />
              )
            }
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important",
              "& .MuiAccordionSummary-content": {
                color: "#fff !important", 
              },
            }}
          >
            Filters
          </AccordionSummary>

          <AccordionDetails>
            <div className="space-y-4">
              <div className="flex gap-5 w-full">
                <div className="flex-1">
                  <ThinDropdown required={false}
                    label="Village Name"
                    value={filterVillageName}
                    onChange={(e) => setFilterVillageName(e.target.value)}
                    options={villageOptions}
                    placeholder="Select Village"
                  />
                </div>

                <div className="flex-1">
                  <ThinDropdown required={false}
                    label="Condition Observed"
                    value={filterCondition}
                    onChange={(e) => setFilterCondition(e.target.value)}
                    options={conditionOptions}
                    placeholder="Select Condition"
                  />
                </div>

                <div className="flex-1">
                  <ThinDropdown required={false}
                    label="Functional Status"
                    value={filterFunctionalStatus}
                    onChange={(e) => setFilterFunctionalStatus(e.target.value)}
                    options={functionalStatusOptions}
                    placeholder="Select Status"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-4 justify-end">
                <button
                  onClick={() => {
                    setFilterVillageName("");
                    setFilterCondition("");
                    setFilterFunctionalStatus("");
                  }}
                  className="px-4 py-2 bg-[#be9609] text-white rounded-lg hover:bg-[#a68108] transition-colors"
                >
                  Clear
                </button>

                <button
                  onClick={handleApply}
                  className="px-4 py-2 bg-[#137e81] text-white rounded-lg transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>

      <div className="rounded-lg my-[30px] shadow w-full bg-white">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="text-[0.875rem] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Manage Monitoring Phase
          </h1>
        </div>

        <div className="p-[1.25rem] w-full">
          <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-[6px] w-full">
            <PaginationDropdown
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
              ]}
            />

            <input
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              value={search}
              placeholder="Search records..."
              type="search"
              className="font-[Light] text-[14px] flex items-center bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md px-3 py-1 outline-none"
            />
          </div>

          <DataTable
            columns={columns}
            persistTableHead
            data={paginatedRecords}
            customStyles={customStyles}
            highlightOnHover
            progressPending={false}
          />

          <div className="flex md:flex-row flex-col justify-between items-center">
            <GeneralFooter
              pagination={pagination}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
              dataLength={pagination.totalItems}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitoringPhaseListing;